/**
 * Admin Layout — slim top bar (logo left, user right) + icon rail on the left.
 * Content fills the rest; pages that want full-bleed (the projects map) render
 * an absolutely-positioned child inside <main>, which is position: relative.
 */

import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import coreskanLogo from '../../assets/coreskan-logo.png';

export const ADMIN_TOPBAR_HEIGHT = 64;
export const ADMIN_RAIL_WIDTH = 60;

const NAV: Array<{ to: string; label: string; icon: JSX.Element }> = [
  {
    to: '/admin/dashboard', label: 'Dashboard',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="8" height="8" rx="2"/><rect x="13" y="3" width="8" height="8" rx="2"/><rect x="3" y="13" width="8" height="8" rx="2"/><rect x="13" y="13" width="8" height="8" rx="2"/></svg>,
  },
  {
    to: '/admin/projects', label: 'Projects',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  },
  {
    to: '/admin/clients', label: 'Clients & Users',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setMobileNavOpen(false); setUserMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const initials = (user?.name || user?.email || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const roleLabel = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '';

  return (
    <div style={styles.shell}>
      {/* ───── Top bar ───── */}
      <header style={styles.topbar}>
        <div style={styles.topbarLeft}>
          {isMobile && (
            <button onClick={() => setMobileNavOpen((v) => !v)} style={styles.iconBtn} aria-label="Menu">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
          )}
          <img src={coreskanLogo} alt="CoreSkan" style={styles.logo} />
        </div>

        <div style={styles.topbarRight}>
          <a href="/portal" target="_blank" rel="noreferrer" style={styles.iconBtn} title="Open client portal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          </a>
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <button onClick={() => setUserMenuOpen((v) => !v)} style={styles.userBtn}>
              {!isMobile && (
                <span style={styles.userText}>
                  <span style={styles.userName}>{user?.name || user?.email}</span>
                  <span style={styles.userRole}>{roleLabel}</span>
                </span>
              )}
              <span style={{ ...styles.avatar, ...(user?.profilePhotoPath ? { backgroundImage: `url(${user.profilePhotoPath})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}) }}>
                {!user?.profilePhotoPath && initials}
              </span>
            </button>
            {userMenuOpen && (
              <div style={styles.userMenu}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{ color: '#f5f5f7', fontSize: 13, fontWeight: 600 }}>{user?.name || '—'}</div>
                  <div style={{ color: '#a1a1a6', fontSize: 12 }}>{user?.email}</div>
                </div>
                <button onClick={logout} style={styles.menuItem}>Sign out</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div style={styles.body}>
        {/* ───── Icon rail (desktop) ───── */}
        {!isMobile && (
          <nav style={styles.rail} aria-label="Admin navigation">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                title={item.label}
                style={({ isActive }) => ({ ...styles.railLink, ...(isActive ? styles.railLinkActive : {}) })}
              >
                {item.icon}
              </NavLink>
            ))}
          </nav>
        )}

        {/* ───── Mobile nav sheet ───── */}
        {isMobile && mobileNavOpen && (
          <div style={styles.mobileNav}>
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} style={({ isActive }) => ({ ...styles.mobileNavLink, ...(isActive ? styles.mobileNavLinkActive : {}) })}>
                {item.icon}<span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        )}

        {/* ───── Content ───── */}
        <main style={{ ...styles.main, padding: isMobile ? 16 : 24 }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const HAIRLINE = '1px solid rgba(255,255,255,0.08)';

const styles: Record<string, React.CSSProperties> = {
  shell: { display: 'flex', flexDirection: 'column', height: '100vh', background: '#0d0d0d', color: '#e2e8f0', fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Inter, system-ui, sans-serif' },
  topbar: {
    height: ADMIN_TOPBAR_HEIGHT, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 16px 0 22px', background: '#161618', borderBottom: HAIRLINE, zIndex: 20,
  },
  topbarLeft: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, padding: '10px 0' },
  logo: { height: 36, display: 'block' },
  topbarRight: { display: 'flex', alignItems: 'center', gap: 6 },
  iconBtn: {
    width: 34, height: 34, borderRadius: 8, border: 'none', background: 'transparent', color: '#a1a1a6', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none',
  },
  userBtn: { display: 'flex', alignItems: 'center', gap: 10, background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 4px 4px 8px', borderRadius: 10 },
  userText: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.15 },
  userName: { color: '#f5f5f7', fontSize: 13, fontWeight: 600, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userRole: { color: '#8e8e93', fontSize: 11 },
  avatar: {
    width: 30, height: 30, borderRadius: '50%', background: 'rgba(79,124,255,0.18)', border: '1px solid rgba(255,255,255,0.12)',
    color: '#8ab0ff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  userMenu: {
    position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 220, background: 'rgba(28,28,30,0.96)', border: HAIRLINE, borderRadius: 12,
    boxShadow: '0 18px 48px rgba(0,0,0,0.55)', overflow: 'hidden', backdropFilter: 'blur(20px)', zIndex: 30,
  },
  menuItem: { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: '#f5f5f7', padding: '10px 14px', fontSize: 13, cursor: 'pointer' },
  body: { display: 'flex', flex: 1, minHeight: 0, position: 'relative' },
  rail: {
    width: ADMIN_RAIL_WIDTH, flexShrink: 0, background: '#1f2023', borderRight: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '4px 0 16px rgba(0,0,0,0.35)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '14px 0', zIndex: 10,
  },
  railLink: { width: 42, height: 42, borderRadius: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#a1a1a6', textDecoration: 'none', transition: 'background 0.15s, color 0.15s' },
  railLinkActive: { background: 'rgba(79,124,255,0.22)', color: '#9dbcff', boxShadow: 'inset 0 0 0 1px rgba(79,124,255,0.35)' },
  mobileNav: {
    position: 'absolute', top: 0, left: 0, right: 0, zIndex: 25, background: 'rgba(20,20,22,0.97)', borderBottom: HAIRLINE,
    display: 'flex', flexDirection: 'column', padding: 8, backdropFilter: 'blur(16px)',
  },
  mobileNavLink: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, color: '#a1a1a6', textDecoration: 'none', fontSize: 14, fontWeight: 500 },
  mobileNavLinkActive: { background: 'rgba(79,124,255,0.16)', color: '#8ab0ff' },
  main: { flex: 1, minWidth: 0, overflow: 'auto', position: 'relative' },
};
